import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  Building2,
  CircleDollarSign,
  ClipboardList,
  ContactRound,
  Gauge,
  Inbox,
  LogOut,
  MessageSquareText,
  Radar,
  RefreshCcw,
  ShoppingBag,
  Target,
  Database,
} from 'lucide-react';
import QuoteBuilder from './QuoteBuilder';
import ReadinessPanel from './ReadinessPanel';
import OrderManager from './OrderManager';
import TaskManager from './TaskManager';
import { prioritizeB2BTasks, overdueB2BTaskSummary } from './b2bTaskSort';
import { pendingQuoteAttention } from './b2bQuoteAttention';
import { pendingPaymentReviews } from './b2bPaymentAttention';
import AdminDetail from './AdminDetail';
import CommunicationCenter from './CommunicationCenter';
import ContactManager from './ContactManager';
import AutomationCenter from './AutomationCenter';
import DiscoveryCenter from './DiscoveryCenter';
import ConversionFunnel from './ConversionFunnel';
import InboundGrowth from './InboundGrowth';
import ProspectActionQueue from './ProspectActionQueue';
import ShippingManager from './ShippingManager';
import SupplierPricingManager from './SupplierPricingManager';
import {
  activityTypeLabel,
  customerTypeLabel,
  priorityLabel,
  requestTypeLabel,
  statusLabel,
  systemText,
  zhDate,
} from './adminI18n';

type Row = Record<string, unknown>;
type AdminData = {
  ok?: boolean;
  metrics: { newLeads: number; openInquiries: number; quotesWaiting: number; activeOrders: number };
  leads: Row[];
  inquiries: Row[];
  companies: Row[];
  contacts: Row[];
  quotes: Row[];
  orders: Row[];
  tasks: Row[];
  activities: Row[];
  pipeline: Row[];
};

const emptyData: AdminData = {
  metrics: { newLeads: 0, openInquiries: 0, quotesWaiting: 0, activeOrders: 0 },
  leads: [], inquiries: [], companies: [], contacts: [], quotes: [], orders: [], tasks: [], activities: [], pipeline: [],
};

const primaryNav = [
  ['/app', Gauge, '工作台'],
  ['/app/inquiries', Inbox, '询盘'],
  ['/app/quotes', CircleDollarSign, '报价单'],
  ['/app/orders', ShoppingBag, '订单'],
  ['/app/tasks', ClipboardList, '待办任务'],
] as const;

// Keep specialist tools available without making them compete with daily sales work.
const navGroups = [
  { id: 'customers', label: '客户管理', items: [
    ['/app/leads', Target, '潜在客户'],
    ['/app/companies', Building2, '客户公司'],
    ['/app/contacts', ContactRound, '联系人'],
    ['/app/communications', MessageSquareText, '沟通记录'],
  ] },
  { id: 'growth', label: '获客与分析', items: [
    ['/app/discovery', Radar, '客户发现'],
    ['/app/funnel', BarChart3, '转化分析'],
    ['/app/growth', BarChart3, '主动询盘与曝光'],
    ['/app/automation', Bot, '自动化'],
  ] },
  { id: 'operations', label: '运营设置', items: [
    ['/app/readiness', ClipboardList, '收款与运营检查'],
    ['/app/suppliers', Database, '供应商采购价'],
    ['/app/shipping', ShoppingBag, '国际运费'],
  ] },
] as const;

const text = (value: unknown, fallback = '—') => value === null || value === undefined || value === '' ? fallback : String(value);

function Login({ onLogin, busy, error }: { onLogin: (key: string) => void; busy: boolean; error: string }) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const key = String(data.get('key') || '').trim();
    if (key) onLogin(key);
  }

  return <div className="admin-login-shell">
    <form className="admin-login-card" onSubmit={submit}>
      <div className="brand-mark">ME</div>
      <span className="eyebrow">MING EAGLE 运营中心</span>
      <h1>销售增长引擎</h1>
      <p>内部销售、报价、订单、履约和客户跟进工作台。</p>
      <label>管理员访问密码<input name="key" type="password" autoComplete="current-password" placeholder="请输入管理员访问密码" required /></label>
      <button className="button" disabled={busy}>{busy ? '正在验证…' : '进入后台'}</button>
      {error && <div className="form-status error"><strong>暂时无法进入后台</strong><p>{error}</p></div>}
      <small>访问密码只保存在当前浏览器会话中，不会写入网站代码。</small>
    </form>
  </div>;
}

function Layout({ children, onRefresh, onLogout, loading }: { children: React.ReactNode; onRefresh: () => void; onLogout: () => void; loading: boolean }) {
  const location = useLocation();
  const activeGroupId = navGroups.find(group => group.items.some(([path]) =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  ))?.id;
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (activeGroupId) setExpandedGroups(current =>
      current[activeGroupId] === true ? current : { ...current, [activeGroupId]: true }
    );
  }, [activeGroupId]);

  return <div className="admin-shell">
    <aside className="sidebar">
      <Link to="/" className="admin-brand"><span className="brand-mark">ME</span><div><strong>MING EAGLE</strong><small>销售工作台</small></div></Link>
      <div className="side-section">常用操作</div>
      <nav className="primary-nav">{primaryNav.map(([path, Icon, label]) => <NavLink end={path === '/app'} to={path} key={path}><Icon size={17}/>{label}</NavLink>)}</nav>
      <div className="side-section">其他功能</div>
      {navGroups.map(group => <details
        className={`side-group${activeGroupId === group.id ? ' has-active' : ''}`}
        key={group.id}
        open={expandedGroups[group.id] === true}
        onToggle={event => {
          const isOpen = event.currentTarget.open;
          setExpandedGroups(current => current[group.id] === isOpen ? current : { ...current, [group.id]: isOpen });
        }}
      >
        <summary><span className="side-group-label">{group.label}</span><span className="side-group-count">{group.items.length}</span></summary>
        <nav className="side-group-nav">{group.items.map(([path, Icon, label]) => <NavLink to={path} key={path}><Icon size={16}/>{label}</NavLink>)}</nav>
      </details>)}
      <div className="side-section">账户</div>
      <nav>
        <button className="side-button" onClick={onRefresh}><RefreshCcw size={17}/>{loading ? '正在刷新…' : '刷新数据'}</button>
        <button className="side-button" onClick={onLogout}><LogOut size={17}/>退出登录</button>
      </nav>
      <div className="sidebar-footer"><span>业务流程</span><small>询盘 → 报价 → 订单 → 发货</small></div>
    </aside>
    <main className="admin-main">{children}</main>
  </div>;
}

function Top({ title, description }: { title: string; description: string }) {
  return <header className="admin-top"><div><span className="eyebrow">MING EAGLE 运营中心</span><h1>{title}</h1><p>{description}</p></div></header>;
}

function DataTable({ columns, rows, filterKey }: {
  columns: { key: string; label: string; format?: (row: Row) => React.ReactNode }[];
  rows: Row[];
  filterKey?: string;
}) {
  const [query,setQuery] = useState('');
  const [filter,setFilter] = useState('ALL');
  const options = useMemo(()=>filterKey ? Array.from(new Set(rows.map(row=>text(row[filterKey], '')).filter(Boolean))) : [],[rows,filterKey]);
  const visible = useMemo(()=>{
    const q = query.trim().toLowerCase();
    return rows.filter(row=>{
      const matchesFilter = !filterKey || filter === 'ALL' || text(row[filterKey],'') === filter;
      if (!matchesFilter) return false;
      if (!q) return true;
      return Object.values(row).some(value=>String(value ?? '').toLowerCase().includes(q));
    });
  },[rows,query,filter,filterKey]);

  return <section className="panel table-panel">
    <div className="table-tools searchable-tools"><div><strong>共 {visible.length} 条</strong><span> / 原始 {rows.length} 条</span></div><div className="table-filters"><input value={query} onChange={e=>setQuery(e.target.value)} placeholder="搜索客户、编号、邮箱、状态…"/>{filterKey && <select value={filter} onChange={e=>setFilter(e.target.value)}><option value="ALL">全部状态</option>{options.map(value=><option key={value} value={value}>{statusLabel(value)}</option>)}</select>}</div></div>
    <div className="table-wrap"><table><thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead><tbody>
      {visible.length === 0 && <tr><td colSpan={columns.length}>没有符合条件的记录。</td></tr>}
      {visible.map((row, i) => <tr key={`${text(row.id || row.reference, 'row')}-${i}`}>{columns.map(c => <td key={c.key}>{c.format ? c.format(row) : text(row[c.key])}</td>)}</tr>)}
    </tbody></table></div>
  </section>;
}

function LeadSections({ leads }: { leads: Row[] }) {
  const websiteLeads=leads.filter(row=>String(row.source||'').toUpperCase()==='WEBSITE');
  const sourcedLeads=leads.filter(row=>String(row.source||'').toUpperCase()!=='WEBSITE');
  const columns=[
    {key:'company',label:'公司 / 机构'},
    {key:'contact',label:'联系人'},
    {key:'lead_score',label:'潜客评分'},
    {key:'status',label:'阶段',format:(r:Row)=>statusLabel(r.status)},
    {key:'next_best_action',label:'下一步行动',format:(r:Row)=>systemText(r.next_best_action)},
    {key:'created_at',label:'创建时间',format:(r:Row)=>zhDate(r.created_at)},
    {key:'detail',label:'操作',format:(r:Row)=><div className="lead-actions"><Link to={`/app/leads/${text(r.id)}`}>查看详情</Link><Link to={`/app/communications?leadId=${encodeURIComponent(text(r.id))}`}>开始沟通</Link></div>}
  ];
  return <>
    <section className="panel" aria-label="官网主动询盘优先处理">
      <div className="panel-head"><h2>① 官网主动询盘 · 优先跟进</h2><span>当前列表 {websiteLeads.length} 条</span></div>
      <p>学校、政府机构、企业及其他客户主动提交的网站线索优先核实。来源标记为 WEBSITE 不代表身份、订单或采购意向已经验证。</p>
    </section>
    <DataTable rows={websiteLeads} filterKey="status" columns={columns}/>
    <section className="panel" aria-label="公开来源待开发客户">
      <div className="panel-head"><h2>② 公开来源与其他潜在客户</h2><span>当前列表 {sourcedLeads.length} 条</span></div>
      <p>这组线索与主动询盘分开管理。开发信必须人工审阅，不能因系统自动发现线索就认定客户有采购意向。</p>
    </section>
    <ProspectActionQueue leads={sourcedLeads}/>
    <DataTable rows={sourcedLeads} filterKey="status" columns={columns}/>
  </>;
}

function Dashboard({ data }: { data: AdminData }) {
  const failedNotices=data.inquiries.filter(row=>row.notification_status==='FAILED').length;
  const quoteAttentionItems=pendingQuoteAttention(data.quotes);
  const paymentReviewOrders=pendingPaymentReviews(data.orders);
  const urgentTasks=prioritizeB2BTasks(data.tasks).slice(0,8);
  const overdue=overdueB2BTaskSummary(data.tasks);
  const metrics = [
    ['新增潜客', data.metrics.newLeads, '最近 7 天新建'],
    ['待处理询盘', data.metrics.openInquiries, '需要审核或回复'],
    ['待跟进报价', data.metrics.quotesWaiting, '已发送或客户已查看'],
    ['进行中订单', data.metrics.activeOrders, '尚未完成或取消'],
  ];

  return <>
    <Top title="仪表盘" description="实时查看销售漏斗、跟进任务、报价、订单和客户动态。"/>
    <section className="metric-grid">{metrics.map(([label, value, note]) => <div className="metric" key={String(label)}><span>{label}</span><strong>{value}</strong><small>{note}</small></div>)}</section>
    {failedNotices>0 && <section className="panel" role="status"><strong>⚠️ {failedNotices} 条询盘的邮件通知未获服务商确认</strong><p>客户需求已经保存到CRM，但邮件通知可能未送达。请立即核实并人工处理。</p><Link to="/app/inquiries">查看相关询盘</Link></section>}
    {overdue.total>0 && <section className="panel" aria-label="逾期跟进任务提醒" role="status">
      <div className="panel-head"><h2>逾期跟进任务</h2><span>当前已加载的待办：{overdue.total} 项逾期</span></div>
      <p>客户主动询盘或报价回复 {overdue.customerOrQuote} 项 · 开发与外联跟进 {overdue.outreach} 项 · 其他待办 {overdue.other} 项。先处理真实客户和报价，不要让开发任务挤占回复时间。</p>
      <p>这里是只读提醒，未自动联系客户、延后任务或修改销售状态；如有旧测试任务，请到任务管理中人工核实后再归档。</p>
      <Link to="/app/tasks">打开跟进任务 · 按优先级查看</Link>
    </section>}
    {paymentReviewOrders.length>0 && <section className="panel" aria-label="待核实收款订单">
      <div className="panel-head"><h2>待核实收款订单</h2><span>{paymentReviewOrders.length} 笔需要人工检查</span></div>
      <p>报价转订单不代表货款到账。请核对银行、Stripe 或其他实际收款记录，以及订单付款状态；在确认符合发货条件前不要安排发货。此提醒不会自动联系客户或改变付款状态。</p>
      {paymentReviewOrders.slice(0,6).map(order=><div className="list-row" key={String(order.id||order.reference)}>
        <div><strong>{text(order.reference)} · {text(order.payment_status)} </strong><small>订单状态：{text(order.status)} · 需人工核实真实收款</small></div>
        <Link className="table-action" to="/app/orders">查看订单</Link>
      </div>)}
      {paymentReviewOrders.length>6&&<p>另有 {paymentReviewOrders.length-6} 笔需在订单管理中核查。</p>}
    </section>}
    {quoteAttentionItems.length>0 && <section className="panel" aria-label="批发报价待处理">
      <div className="panel-head"><h2>批发报价待处理</h2><span>{quoteAttentionItems.length} 项需人工检查</span></div>
      <p>依次核实报价过期、客户已查看未反馈、已发送未确认及久未审核的草稿。不要自动催单，也不要把已查看或已转化误认为已付款。</p>
      {quoteAttentionItems.slice(0,6).map(item=><div className="list-row" key={String(item.quote.id||item.quote.reference)}>
        <div><strong>{text(item.quote.reference)} · {item.label}</strong><small>{item.note}</small></div>
        <Link className="table-action" to="/app/quotes">查看报价</Link>
      </div>)}
      {quoteAttentionItems.length>6&&<p>另有 {quoteAttentionItems.length-6} 项需在报价管理中处理。</p>}
      <Link to="/app/quotes">打开报价管理 · 人工审核</Link>
    </section>}
    <section className="admin-grid two-col">
      <div className="panel"><div className="panel-head"><h2>下一步行动</h2><span>自动化 + 人工跟进</span></div>
        {data.tasks.length === 0 && <div className="empty-row">当前没有待处理任务。</div>}
        {urgentTasks.map((task, i) => <div className="list-row" key={text(task.id, String(i))}><div><strong>{systemText(task.title)}</strong><small>{text(task.related_to)} · 截止 {zhDate(task.due_at)}</small></div><span className={`priority ${text(task.priority).toLowerCase()}`}>{priorityLabel(task.priority)}</span></div>)}
      </div>
      <div className="panel"><div className="panel-head"><h2>潜客销售漏斗</h2><BarChart3 size={18}/></div><div className="pipeline">
        {data.pipeline.length === 0 && <div><span>暂无潜客</span><strong>0</strong></div>}
        {data.pipeline.map((item, i) => <div key={`${text(item.status)}-${i}`}><span>{statusLabel(item.status)}</span><strong>{text(item.value, '0')}</strong></div>)}
      </div></div>
    </section>
    <section className="panel"><div className="panel-head"><h2>最近动态</h2><span>客户共享时间线</span></div>
      {data.activities.length === 0 && <div className="empty-row">暂无业务动态。</div>}
      {data.activities.map((activity, i) => <div className="activity" key={`${text(activity.activity_type)}-${i}`}><span/><div><strong>{systemText(activity.title) || activityTypeLabel(activity.activity_type)}</strong><p>{systemText(activity.description)}</p></div><small>{zhDate(activity.created_at)}</small></div>)}
    </section>
  </>;
}

export default function AdminApp() {
  const location = useLocation();
  const [key, setKey] = useState(() => sessionStorage.getItem('mingeagle_admin_key') || '');
  const [data, setData] = useState<AdminData>(emptyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authorized, setAuthorized] = useState(false);

  const [authBusy, setAuthBusy] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Data refresh is distinct from authentication. A D1 outage must not turn
  // a correctly authenticated session into a misleading "wrong password" page.
  const load = useCallback(async (accessKey: string) => {
    if (!accessKey) return;
    setLoading(true); setError('');
    try {
      const response = await fetch('/api/admin/data', { headers: { 'x-admin-key': accessKey }, signal: AbortSignal.timeout(15000) });
      if (response.status === 401 || response.status === 403) {
        setAuthorized(false);
        setKey('');
        sessionStorage.removeItem('mingeagle_admin_key');
        throw new Error('登录已失效，请重新验证管理员密码。');
      }
      const body = await response.json().catch(() => ({ ok: false, error: '服务器暂时不可用，请稍后重试。' })) as AdminData & { error?: string; code?: string; resetAt?: string };
      if (body.code === 'DATABASE_DAILY_LIMIT') throw new Error('数据库今日读取额度已用完。下次重置：' + (body.resetAt ? new Date(body.resetAt).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) + '（北京时间）' : '每日北京时间 08:00') + '。请勿反复刷新。');
      if (!response.ok || !body.ok) throw new Error(body.error || '业务数据暂时无法加载。');
      setData(body);
      setDataLoaded(true);
    } catch (err) {
      setError(err instanceof Error && err.name === 'TimeoutError' ? '业务数据加载超时，请稍后点“刷新数据”重试。' : err instanceof Error ? err.message : '业务数据暂时无法加载。');
    } finally { setLoading(false); }
  }, []);

  const login = useCallback(async (accessKey: string) => {
    if (!accessKey) return;
    setAuthBusy(true); setError('');
    try {
      const response = await fetch('/api/admin/session', { headers: { 'x-admin-key': accessKey }, signal: AbortSignal.timeout(15000) });
      const body = await response.json().catch(() => ({ ok: false, error: '登录服务暂时不可用。' })) as { ok?: boolean; error?: string };
      if (!response.ok || !body.ok) throw new Error(body.error || '密码验证失败。');
      setAuthorized(true);
      setKey(accessKey);
      sessionStorage.setItem('mingeagle_admin_key', accessKey);
      void load(accessKey);
    } catch (err) {
      setAuthorized(false);
      sessionStorage.removeItem('mingeagle_admin_key');
      setError(err instanceof Error && err.name === 'TimeoutError' ? '登录服务超时，请稍后重试。' : err instanceof Error ? err.message : '无法验证管理员密码。');
    } finally { setAuthBusy(false); }
  }, [load]);

  useEffect(() => { if (key) void login(key); }, []);

  const segments = useMemo(() => location.pathname.replace(/^\/app\/?/, '').split('/').filter(Boolean), [location.pathname]);
  const page = segments[0] || 'dashboard';
  const detailId = segments[1] || '';

  if (!authorized) return <Login onLogin={k => void login(k)} busy={authBusy} error={error}/>;

  let content: React.ReactNode;
  if (page === 'dashboard' && !dataLoaded) content = <><Top title="仪表盘" description="销售数据暂时未加载，管理员登录状态不受影响。"/><section className="panel"><p>{loading ? '正在加载业务数据…' : '暂时无法读取销售、订单和客户数据。请查看页面提示，并在数据库恢复后点击左侧“刷新数据”。'}</p></section></>;
  else if (page === 'dashboard') content = <Dashboard data={data}/>;
  else if (page === 'discovery') content = <>
    <Top title="客户发现" description="选择地区和客户类型，一键完成搜索、官网核验、公开信息补全和待开发客户入库。"/>
    <DiscoveryCenter accessKey={key} onChanged={() => void load(key)} />
  </>;
  else if (page === 'funnel') content = <>
    <Top title="客户转化分析" description="真实客户分层、联系方式覆盖、已联系、客户回复、报价与订单，帮助确定优先开发对象。"/>
    <ConversionFunnel accessKey={key}/>
  </>;
  else if (page === 'growth') content = <>
    <Top title="主动询盘与曝光" description="分辨 B2B/B2C 已提交询盘的真实来源，并准备连接搜索曝光和网站访问统计。"/>
    <InboundGrowth accessKey={key}/>
  </>;
  else if (page === 'leads' && detailId) content = <AdminDetail key={`${page}-${detailId}`} type="lead" id={detailId} accessKey={key}/>;
  else if (page === 'inquiries' && detailId) content = <AdminDetail key={`${page}-${detailId}`} type="inquiry" id={detailId} accessKey={key}/>;
  else if (page === 'companies' && detailId) content = <AdminDetail key={`${page}-${detailId}`} type="company" id={detailId} accessKey={key}/>;
  else if (page === 'leads') content = <><Top title="潜在客户" description="先处理官网真实主动询盘，再核实公开来源待开发客户。所有外联邮件须人工审阅。"/><LeadSections leads={data.leads}/></>;
  else if (page === 'inquiries') content = <><Top title="询盘" description="网站提交的每条询盘都会自动进入销售流程并形成结构化记录。"/><DataTable filterKey="status" rows={data.inquiries} columns={[
    {key:'reference',label:'询盘编号'},{key:'customer',label:'客户'},{key:'request_type',label:'询盘类型',format:r=>requestTypeLabel(r.request_type)},{key:'estimated_quantity',label:'预计数量'},{key:'status',label:'状态',format:r=>statusLabel(r.status)},{key:'notification_status',label:'通知状态',format:r=>String(r.notification_status)==='FAILED'?'⚠️ 邮件通知未确认 · 请查CRM':String(r.notification_status)==='ACCEPTED'?'服务已接收（非送达确认）':'未记录（历史/导入）'},{key:'created_at',label:'创建时间',format:r=>zhDate(r.created_at)},{key:'detail',label:'操作',format:r=><Link to={`/app/inquiries/${text(r.id)}`}>查看详情</Link>}
  ]}/></>;
  else if (page === 'companies') content = <><Top title="客户公司" description="管理客户组织、客户类型、国家地区和生命周期阶段。"/><DataTable filterKey="status" rows={data.companies} columns={[
    {key:'name',label:'公司 / 机构'},{key:'customer_type',label:'客户类型',format:r=>customerTypeLabel(r.customer_type)},{key:'country',label:'国家'},{key:'city',label:'城市'},{key:'status',label:'阶段',format:r=>statusLabel(r.status)},{key:'created_at',label:'创建时间',format:r=>zhDate(r.created_at)},{key:'detail',label:'操作',format:r=><Link to={`/app/companies/${text(r.id)}`}>查看详情</Link>}
  ]}/></>;
  else if (page === 'contacts') content = <>
    <Top title="联系人" description="补录负责人、采购联系人、电话和 WhatsApp，并管理禁止联系状态。"/>
    <ContactManager contacts={data.contacts} accessKey={key} onChanged={() => void load(key)} />
  </>;
  else if (page === 'communications') content = <>
    <Top title="沟通中心" description="使用免费渠道开展邮件 / WhatsApp 跟进，登记客户回复，并自动推进潜客阶段和下一步任务。"/>
    <CommunicationCenter accessKey={key} onChanged={() => void load(key)} />
  </>;
  else if (page === 'automation') content = <>
    <Top title="自动化中心" description="集中控制暖客户自动跟进、发送上限、待审核队列和自动化运行状态。"/>
    <AutomationCenter accessKey={key} />
  </>;
  else if (page === 'quotes') content = <>
    <Top title="报价单" description="创建报价草稿、生成客户安全链接、跟踪查看状态并自动转订单。"/>
    <QuoteBuilder inquiries={data.inquiries} quotes={data.quotes} accessKey={key} loading={loading} dataError={error} onCreated={() => load(key)} />
  </>;
  else if (page === 'readiness') content = <>
    <Top title="生产与 B2B Stripe 收款验收" description="逐单显示卖方报价、买方同意、美国目的地、税务依据、支付凭证和 Webhook 阻塞项。所有检查只读，不会自动收款。"/>
    <ReadinessPanel accessKey={key}/>
  </>;
  else if (page === 'suppliers') content = <><Top title="供应商采购价资料库" description="人工核实 SKU、MOQ、整箱倍数、美元采购价阶梯、报价有效期和审核版本；不自动作为对外售价。"/><SupplierPricingManager accessKey={key}/></>;
  else if (page === 'shipping') content = <><Top title="国际运费管理" description="按国家和运输方式维护抛货运价、包装尺寸和启用状态。"/><ShippingManager accessKey={key}/></>;
  else if (page === 'orders') content = <>
    <Top title="订单" description="确认收款、处理订单、录入物流、确认送达，并自动进入复购跟进。"/>
    <OrderManager orders={data.orders} accessKey={key} onChanged={() => void load(key)} />
  </>;
  else content = <>
    <Top title="跟进任务" description="执行、延期或完成由销售和履约自动化产生的待办任务。"/>
    <TaskManager tasks={data.tasks} inquiries={data.inquiries} accessKey={key} onChanged={() => void load(key)} />
  </>;

  return <Layout loading={loading} onRefresh={() => void load(key)} onLogout={() => { sessionStorage.removeItem('mingeagle_admin_key'); setKey(''); setAuthorized(false); setData(emptyData); }}>{error && <div role="alert" className="form-status error">{error}</div>}{content}</Layout>;
}
